import { useState, useEffect, useCallback, useMemo } from "react";
import { useAtom, useAtomValue } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { ReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import { getHoldings } from "@/api/Holdings";
import { getReportingPeriods } from "@/api/ReportingPeriods";
import { HoldingWithInstrumentAndAccount } from "@/types";
import { ReportingPeriod } from "@/types/ReportingPeriod";

interface PeriodHoldingsData {
  period: ReportingPeriod;
  holdings: HoldingWithInstrumentAndAccount[];
}

export function useMultiPeriodCurrencyController() {
  const [selectedPeriods, setSelectedPeriods] = useState<ReportingPeriod[]>([]);
  const [periodHoldingsData, setPeriodHoldingsData] = useState<PeriodHoldingsData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [periodsWithHoldings, setPeriodsWithHoldings] = useState<ReportingPeriod[]>([]);

  const selectedFund = useAtomValue(SelectedFundAtom);
  const [reportingPeriods, setReportingPeriods] = useAtom(ReportingPeriodAtom);

  // Fetch reporting periods when component loads
  const {
    data: periods = [],
    isLoading: isLoadingPeriods,
  } = useQuery({
    queryKey: ["reportingPeriods", selectedFund?.fundId],
    queryFn: async () => {
      console.log('Fetching reporting periods for multi-period analysis:', selectedFund?.fundId);
      const data = await getReportingPeriods(
        selectedFund?.fundId?.toString() || ""
      );
      setReportingPeriods(data);
      return data;
    },
    enabled: !!selectedFund?.fundId,
  });

  // Sort periods chronologically
  const sortedPeriods = useMemo(() => {
    return [...periods].sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime());
  }, [periods]);

  // Filter periods that have holdings
  useEffect(() => {
    const fetchPeriodsWithHoldings = async () => {
      if (!selectedFund || sortedPeriods.length === 0) {
        setPeriodsWithHoldings([]);
        return;
      }

      console.log('Checking which periods have holdings...');
      try {
        const holdingsPromises = sortedPeriods.map(async (period) => {
          try {
            const holdings = await getHoldings(
              selectedFund.fundId,
              parseInt(period.reportingPeriodId)
            );
            return { period, hasHoldings: holdings.length > 0 };
          } catch (error) {
            console.warn(`Failed to fetch holdings for period ${period.reportingPeriodId}:`, error);
            return { period, hasHoldings: false };
          }
        });

        const results = await Promise.all(holdingsPromises);
        const periodsWithData = results
          .filter(result => result.hasHoldings)
          .map(result => result.period);
        
        console.log(`Found ${periodsWithData.length} periods with holdings out of ${sortedPeriods.length} total periods`);
        setPeriodsWithHoldings(periodsWithData);
      } catch (error) {
        console.error("Error checking periods for holdings:", error);
        setPeriodsWithHoldings([]);
      }
    };

    fetchPeriodsWithHoldings();
  }, [selectedFund, sortedPeriods]);

  // Fetch holdings for all selected periods
  const fetchAllPeriodHoldings = useCallback(async () => {
    if (!selectedFund || selectedPeriods.length === 0) {
      setPeriodHoldingsData([]);
      return;
    }

    setIsLoading(true);
    try {
      console.log('Fetching holdings for multiple periods:', selectedPeriods.length);
      
      const holdingsPromises = selectedPeriods.map(async (period) => {
        const holdings = await getHoldings(
          selectedFund.fundId,
          parseInt(period.reportingPeriodId)
        );
        return { period, holdings };
      });

      const results = await Promise.all(holdingsPromises);
      
      // Sort results by period date
      const sortedResults = results.sort((a, b) => 
        new Date(a.period.period).getTime() - new Date(b.period.period).getTime()
      );
      
      console.log('Multi-period holdings fetched:', sortedResults.length, 'periods');
      setPeriodHoldingsData(sortedResults);
    } catch (error) {
      console.error("Error fetching multi-period holdings:", error);
      setPeriodHoldingsData([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFund, selectedPeriods]);

  useEffect(() => {
    fetchAllPeriodHoldings();
  }, [fetchAllPeriodHoldings]);

  // Add a period to selection
  const addPeriod = useCallback((period: ReportingPeriod) => {
    setSelectedPeriods(prev => {
      if (prev.find(p => p.reportingPeriodId === period.reportingPeriodId)) {
        return prev; // Period already selected
      }
      return [...prev, period];
    });
  }, []);

  // Remove a period from selection
  const removePeriod = useCallback((periodId: string) => {
    setSelectedPeriods(prev => prev.filter(p => p.reportingPeriodId !== periodId));
  }, []);

  // Clear all selected periods
  const clearAllPeriods = useCallback(() => {
    setSelectedPeriods([]);
  }, []);

  // Select multiple periods at once (e.g., last N periods)
  const selectLastNPeriods = useCallback((n: number) => {
    const lastN = periodsWithHoldings.slice(-n);
    setSelectedPeriods(lastN);
  }, [periodsWithHoldings]);

  // Select all available periods
  const selectAllPeriods = useCallback(() => {
    setSelectedPeriods(periodsWithHoldings);
  }, [periodsWithHoldings]);

  // Check if a period is currently selected
  const isPeriodSelected = useCallback((periodId: string) => {
    return selectedPeriods.some(p => p.reportingPeriodId === periodId);
  }, [selectedPeriods]);

  return {
    selectedPeriods,
    periodHoldingsData,
    availablePeriods: periodsWithHoldings,
    isLoading: isLoading || isLoadingPeriods,
    searchQuery,
    setSearchQuery,
    addPeriod,
    removePeriod,
    clearAllPeriods,
    selectLastNPeriods,
    selectAllPeriods,
    isPeriodSelected,
  };
} 